import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faFolderOpen,
  faFilePdf,
  faFileWord,
  faFilePowerpoint,
  faFileImage,
  faFileAlt,
  faDownload,
  faCertificate,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';

interface Resource {
  resourceId: string;
  title: string;
  description: string;
  type: number; // 0 Material, 1 Certificate
  fileUrl: string;
  fileExtension: string;
  fileSizeBytes: number;
  learnershipTitle: string | null;
  moduleTitle: string | null;
  uploadedAt: string;
}

interface ResourceGroup {
  groupTitle: string;
  resources: Resource[];
}

function getFileIcon(extension: string) {
  const ext = extension.toLowerCase().replace('.', '');
  if (ext === 'pdf') return faFilePdf;
  if (['doc', 'docx'].includes(ext)) return faFileWord;
  if (['ppt', 'pptx'].includes(ext)) return faFilePowerpoint;
  if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return faFileImage;
  return faFileAlt;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

export default function DownloadsPage() {
  const [groups, setGroups] = useState<ResourceGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { accessToken } = useAuth();

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await fetch('/api/resources/me', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to load downloads');
      const data = await response.json();
      setGroups(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load your downloads. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const totalResources = groups.reduce((sum, g) => sum + g.resources.length, 0);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <h1 className="text-2xl font-bold text-brand-950">Downloads</h1>
        <p className="text-sm text-brand-900/60 mt-1">
          Course materials and certificates available to you
        </p>
      </div>

      <div className="p-8">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && totalResources === 0 && (
          <div className="card-surface p-12 text-center">
            <FontAwesomeIcon icon={faFolderOpen} className="h-12 w-12 text-brand-300 mb-4" />
            <h3 className="text-lg font-bold text-brand-950">No downloads available yet</h3>
            <p className="text-sm text-brand-900/60 mt-2">
              Course materials and certificates will appear here once they're published.
            </p>
          </div>
        )}

        {!isLoading && !error && totalResources > 0 && (
          <div className="space-y-8">
            {groups.map((group) => (
              <div key={group.groupTitle}>
                <h2 className="text-sm font-bold uppercase tracking-wider text-brand-900/50 mb-3 flex items-center gap-2">
                  {group.groupTitle === 'Certificates' && (
                    <FontAwesomeIcon icon={faCertificate} className="h-3.5 w-3.5" />
                  )}
                  {group.groupTitle}
                </h2>
                <div className="card-surface divide-y divide-canvas-200">
                  {group.resources.map((resource) => (
                    <div
                      key={resource.resourceId}
                      className="flex items-center gap-4 p-4 sm:p-5"
                    >
                      <div className="w-11 h-11 rounded-xl bg-brand-50 flex items-center justify-center text-brand-700 flex-shrink-0">
                        <FontAwesomeIcon
                          icon={
                            resource.type === 1 ? faCertificate : getFileIcon(resource.fileExtension)
                          }
                          className="h-5 w-5"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-brand-950 truncate">
                          {resource.title}
                        </p>
                        <p className="text-xs text-brand-900/50 mt-0.5 truncate">
                          {resource.moduleTitle ? `${resource.moduleTitle} · ` : ''}
                          {resource.fileExtension.toUpperCase().replace('.', '')} ·{' '}
                          {formatFileSize(resource.fileSizeBytes)}
                        </p>
                      </div>
                      <a
                        href={resource.fileUrl}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-brand-gradient text-white text-sm font-semibold rounded-lg hover:shadow-lg transition-all flex items-center gap-2 flex-shrink-0"
                      >
                        <FontAwesomeIcon icon={faDownload} className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">Download</span>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
